/* Shop by palette — Glenn Jones Art
   Ranks prints by how closely their artwork colours match a shopper's four chosen colours.
   Colour data comes from glenn-art-finder-catalogue.json (Content > Files), the same catalogue the
   AI Art Finder uses. The section's editor-only "Update catalogue" button adds new prints, re-reads each print
   with the grey backdrop and white border cropped out and rewrites palette, color_hex, colors and
   color_intensity for both tools. Product details are fetched live from /products/<handle>.js. */
(function () {
  'use strict';
  if (window.__GJA_SBP_LOADED) return;
  window.__GJA_SBP_LOADED = true;

  /* ---------- colour maths ---------- */
  var toHex = function (r, g, b) { return [r, g, b].map(function (v) { return Math.round(v).toString(16).padStart(2, '0'); }).join('').toUpperCase(); };
  var fromHex = function (h) { h = h.replace('#', ''); return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]; };
  var lin = function (c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  function lab(r, g, b) {
    var R = lin(r), G = lin(g), B = lin(b);
    var x = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047, y = R * 0.2126 + G * 0.7152 + B * 0.0722, z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
    var f = function (t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; }, fy = f(y);
    return [116 * fy - 16, 500 * (f(x) - fy), 200 * (fy - f(z))];
  }
  function lab2rgb(L, a, b) {
    var fy = (L + 16) / 116, fx = fy + a / 500, fz = fy - b / 200;
    var fi = function (t) { var t3 = t * t * t; return t3 > 0.008856 ? t3 : (t - 16 / 116) / 7.787; };
    var X = 0.95047 * fi(fx), Y = fi(fy), Z = 1.08883 * fi(fz);
    var lr = [3.2406 * X - 1.5372 * Y - 0.4986 * Z, -0.9689 * X + 1.8758 * Y + 0.0415 * Z, 0.0557 * X - 0.2040 * Y + 1.0570 * Z];
    if (lr.some(function (v) { return v < -0.0005 || v > 1.0005; })) return null;
    return lr.map(function (v) { v = Math.min(1, Math.max(0, v)); return 255 * (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055); });
  }
  var dE = function (a, b) { return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]); };
  function rng(a) { return function () { a |= 0; a = a + 0x6D2B79F5 | 0; var t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }

  /* ---------- colour grid: neutral column + 16 hues, 12 lightness steps ---------- */
  function lch(L, C, h) {
    var r = h * Math.PI / 180;
    for (var c = C; c >= 0; c -= 0.5) { var v = lab2rgb(L, c * Math.cos(r), c * Math.sin(r)); if (v) return '#' + toHex(v[0], v[1], v[2]); }
    var g = lab2rgb(L, 0, 0); return '#' + toHex(g[0], g[1], g[2]);
  }
  // Peak chroma and the lightness it occurs at for each of the 16 hues (25deg, 47.5deg ... in Lab),
  // so every hue reaches its own brightest point: yellow up high, blue lower down.
  var CUSPS = [[89,53],[94,59],[82,73],[87,87],[99,93],[114,87],[72,88],[54,89],[46,86],[43,79],[46,71],[59,62],[93,46],[119,48],[96,57],[84,55]];
  var VIVID = 0.8; // 1 = full screen-bright, lower = softer
  var GRID = (function () {
    var out = [], ROWS = 12, HUES = 16;
    for (var row = 0; row < ROWS; row++) {
      var L = 94 - row * (76 / (ROWS - 1));
      out.push(lch(L, 3, 75));
      for (var i = 0; i < HUES; i++) {
        var Cc = CUSPS[i][0], Lc = CUSPS[i][1];
        var shape = L > Lc ? (100 - L) / (100 - Lc) : L / Lc;
        out.push(lch(L, Cc * VIVID * Math.min(1, shape), 25 + i * 360 / HUES));
      }
    }
    return out;
  })();

  /* ---------- artwork colour extraction (used by the index builder) ----------
     Product images show the unframed print on a grey backdrop with a white paper border,
     so the backdrop and border are cropped away before the colours are read. */
  function bbox(W, test, x0, y0, x1, y1) {
    var a = 1e9, b = 1e9, c = -1, d = -1;
    for (var y = y0; y <= y1; y++) for (var x = x0; x <= x1; x++) if (test(x, y)) { if (x < a) a = x; if (y < b) b = y; if (x > c) c = x; if (y > d) d = y; }
    return c < 0 ? null : [a, b, c, d];
  }
  function extract(img) {
    var W = 96, H = Math.max(1, Math.round(96 * img.naturalHeight / img.naturalWidth));
    var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    var cx = cv.getContext('2d', { willReadFrequently: true }); cx.drawImage(img, 0, 0, W, H);
    var d = cx.getImageData(0, 0, W, H).data, L = new Array(W * H), i;
    for (i = 0; i < W * H; i++) L[i] = lab(d[i * 4], d[i * 4 + 1], d[i * 4 + 2]);
    var at = function (x, y) { return L[y * W + x]; };
    var corners = [at(1, 1), at(W - 2, 1), at(1, H - 2), at(W - 2, H - 2)];
    var bg = [0, 1, 2].map(function (k) { return corners.reduce(function (s, c) { return s + c[k]; }, 0) / 4; });
    var box = bbox(W, function (x, y) { return dE(at(x, y), bg) > 6; }, 0, 0, W - 1, H - 1) || [0, 0, W - 1, H - 1];
    var a = box[0], b = box[1], c = box[2], e = box[3], edge = [], x, y;
    for (x = a + 1; x < c; x++) edge.push(at(x, b + 1), at(x, e - 1));
    for (y = b + 1; y < e; y++) edge.push(at(a + 1, y), at(c - 1, y));
    var paper = [0, 1, 2].map(function (k) { return edge.reduce(function (s, v) { return s + v[k]; }, 0) / edge.length; });
    var inner = null;
    if (paper[0] > 85 && Math.hypot(paper[1], paper[2]) < 6) inner = bbox(W, function (x, y) { return dE(at(x, y), paper) > 6; }, a + 1, b + 1, c - 1, e - 1);
    if (inner && (inner[2] - inner[0]) * (inner[3] - inner[1]) > 0.1 * (c - a) * (e - b)) box = inner;
    var pts = [];
    for (y = box[1]; y <= box[3]; y++) for (x = box[0]; x <= box[2]; x++) { i = y * W + x; pts.push({ lab: L[i], rgb: [d[i * 4], d[i * 4 + 1], d[i * 4 + 2]] }); }
    if (pts.length < 20) return [];
    var R = rng(7), K = 6, C = [pts[Math.floor(R() * pts.length)].lab.slice()];
    while (C.length < K) {
      var ds = pts.map(function (p) { var m = 1e9; C.forEach(function (q) { var v = dE(p.lab, q); if (v < m) m = v; }); return m * m; });
      var sum = ds.reduce(function (s, v) { return s + v; }, 0); if (sum < 1e-6) break;
      var t = R() * sum, j = 0; for (; j < ds.length - 1; j++) { t -= ds[j]; if (t <= 0) break; }
      C.push(pts[j].lab.slice());
    }
    var sums = [];
    for (var it = 0; it < 10; it++) {
      sums = C.map(function () { return { l: [0, 0, 0], r: [0, 0, 0], n: 0 }; });
      pts.forEach(function (p) {
        var bi = 0, m = 1e9; C.forEach(function (q, k) { var v = dE(p.lab, q); if (v < m) { m = v; bi = k; } });
        var s = sums[bi]; for (var z = 0; z < 3; z++) { s.l[z] += p.lab[z]; s.r[z] += p.rgb[z]; } s.n++;
      });
      sums.forEach(function (s, k) { if (s.n) C[k] = s.l.map(function (v) { return v / s.n; }); });
    }
    return sums.filter(function (s) { return s.n / pts.length >= 0.02; })
      .map(function (s) { return [toHex(s.r[0] / s.n, s.r[1] / s.n, s.r[2] / s.n), Math.round(100 * s.n / pts.length)]; })
      .sort(function (p, q) { return q[1] - p[1]; });
  }
  function readImage(src) {
    return new Promise(function (ok) {
      var im = new Image(); im.crossOrigin = 'anonymous';
      im.onload = function () { try { ok(extract(im)); } catch (e) { ok(null); } };
      im.onerror = function () { ok(null); };
      src = src.indexOf('//') === 0 ? 'https:' + src : src;
      im.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=160';
    });
  }

  /* ---------- colour words + catalogue fields (shared with the AI Art Finder) ----------
     The art finder reads color_hex, colors and color_intensity; Shop by palette reads palette
     ("HEXww,HEXww,…": artwork colours with % coverage). All are written from the cropped artwork. */
  function colourName(hex) {
    var c = fromHex(hex), v = lab(c[0], c[1], c[2]), L = v[0], C = Math.hypot(v[1], v[2]);
    var h = Math.atan2(v[2], v[1]) * 180 / Math.PI; if (h < 0) h += 360;
    if (C < 8 || (L < 18 && C < 15) || (L > 90 && C < 12)) return L < 22 ? 'black' : L > 86 ? 'white' : 'grey';
    if (h >= 345 || h < 20) return L > 62 ? 'pink' : 'red';
    if (h < 55) return L > 72 ? 'pink' : (L < 45 && C < 45 ? 'brown' : 'red');
    if (h < 85) return (L < 55 || C < 25) ? 'brown' : 'orange';
    if (h < 115) return L < 45 ? 'brown' : 'yellow';
    if (h < 170) return 'green';
    if (h < 225) return 'teal';
    if (h < 315) return 'blue';
    return L > 65 ? 'pink' : 'purple';
  }
  function chromaOf(hex) { var c = fromHex(hex), v = lab(c[0], c[1], c[2]); return Math.hypot(v[1], v[2]); }
  function enrichRecord(r, pal) {
    var out = Object.assign({}, r);
    out.palette = pal.map(function (p) { return p[0] + String(p[1]).padStart(2, '0'); }).join(',');
    out.color_hex = pal.slice(0, 4).map(function (p) { return '#' + p[0]; }).join(', ');
    var tot = {};
    pal.forEach(function (p) { var k = colourName(p[0]); tot[k] = (tot[k] || 0) + p[1]; });
    var names = Object.keys(tot).filter(function (k) { return tot[k] >= ((k === 'white' || k === 'grey') ? 15 : 8); })
      .sort(function (a, b) { return tot[b] - tot[a]; }).slice(0, 4);
    if (!names.length) names = [colourName(pal[0][0])];
    out.colors = names.join(', ');
    var wsum = pal.reduce(function (s, p) { return s + p[1]; }, 0);
    var wc = pal.reduce(function (s, p) { return s + p[1] * chromaOf(p[0]); }, 0) / wsum;
    var maxc = Math.max.apply(null, pal.filter(function (p) { return p[1] >= 8; }).map(function (p) { return chromaOf(p[0]); }).concat([0]));
    out.color_intensity = (wc >= 32 || maxc >= 60) ? 'high' : (wc >= 15 || maxc >= 35) ? 'medium' : 'low';
    if (r.search && r.colors && r.search.indexOf(r.colors) > -1) out.search = r.search.replace(r.colors, out.colors);
    else out.search = [r.subject, r.secondary, r.nz, r.environment, r.style, r.mood, out.colors, (r.title || '').toLowerCase()].filter(Boolean).join(', ');
    return out;
  }

  /* ---------- catalogue ---------- */
  function parsePalette(r) {
    if (r.palette) {
      return String(r.palette).split(',').map(function (t) {
        var hex = t.slice(0, 6), rgb = fromHex(hex);
        return { hex: '#' + hex, w: (+t.slice(6)) / 100, lab: lab(rgb[0], rgb[1], rgb[2]) };
      });
    }
    // Older records without a palette: fall back to color_hex, equally weighted.
    var hexes = String(r.color_hex || '').match(/#[0-9a-fA-F]{6}\b/g) || [];
    return hexes.map(function (h) { var rgb = fromHex(h); return { hex: h.toUpperCase(), w: 1 / hexes.length, lab: lab(rgb[0], rgb[1], rgb[2]) }; });
  }
  var catPromise = null;
  function loadCatalogue(url) {
    if (catPromise) return catPromise;
    catPromise = fetch(url, { cache: 'force-cache', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('Catalogue could not be loaded'); return r.json(); })
      .then(function (cat) {
        return (cat.records || []).map(function (r) { return { handle: r.handle, pal: parsePalette(r) }; })
          .filter(function (r) { return r.handle && r.pal.length; });
      })
      .catch(function (e) { catPromise = null; throw e; });
    return catPromise;
  }

  /* ---------- scoring ----------
     Average distance from each chosen colour to its nearest artwork colour, plus how much of the
     artwork sits far from every chosen colour. Distances are CIE76 in Lab. */
  function score(u, pal) {
    var mc = 0, cc = 0;
    u.forEach(function (c) { var best = 1e9; pal.forEach(function (p) { var v = dE(c, p.lab) + (p.w < 0.05 ? 8 : 0); if (v < best) best = v; }); mc += best; });
    mc /= u.length;
    pal.forEach(function (p) { var best = 1e9; u.forEach(function (c) { best = Math.min(best, dE(c, p.lab)); }); cc += p.w * best; });
    return Math.max(0, Math.min(100, Math.round(100 - (0.6 * mc + 0.4 * cc) * 1.5)));
  }

  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var esc = function (s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
  function sized(src, w) { if (!src) return ''; src = src.indexOf('//') === 0 ? 'https:' + src : src; return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }

  /* ---------- section ---------- */
  function init(root) {
    if (root.__sbp) return; root.__sbp = true;
    var q = function (s) { return root.querySelector(s); };
    var cfg = {
      catalogueUrl: root.dataset.catalogueUrl,
      count: parseInt(root.dataset.resultsCount, 10) || 12,
      showScore: root.dataset.showScore === 'true',
      showPrice: root.dataset.showPrice === 'true',
      hideSoldOut: root.dataset.hideSoldOut !== 'false',
      collection: root.dataset.collection || 'art-prints',
      design: root.dataset.designMode === 'true',
      money: new Intl.NumberFormat('en-NZ', { style: 'currency', currency: root.dataset.currency || 'NZD' })
    };
    var slots = [null, null, null, null], active = null, ranked = [], shown = 0;
    var blocksEl = q('[data-blocks]'), find = q('[data-find]'), picker = q('[data-picker]'), coloursEl = q('[data-colours]');
    var results = q('[data-results]'), list = q('[data-list]'), more = q('[data-more]'), status = q('[data-status]'), mini = q('[data-mini]');

    GRID.forEach(function (hex) {
      var b = document.createElement('button'); b.type = 'button'; b.className = 'sbp__sw';
      b.style.background = hex; b.dataset.hex = hex; b.title = hex; b.setAttribute('aria-label', hex);
      coloursEl.appendChild(b);
    });

    function renderBlocks() {
      blocksEl.innerHTML = '';
      slots.forEach(function (hex, i) {
        var b = document.createElement('button'); b.type = 'button';
        b.className = 'sbp__block ' + (hex ? 'sbp__block--filled' : 'sbp__block--empty') + (active === i ? ' sbp__block--active' : '');
        if (hex) { b.style.background = hex; b.title = hex; b.setAttribute('aria-label', 'Colour ' + (i + 1) + ': ' + hex + '. Change'); }
        else b.setAttribute('aria-label', 'Choose colour ' + (i + 1));
        b.addEventListener('click', function () { active === i ? closePicker() : openPicker(i); });
        blocksEl.appendChild(b);
      });
      find.disabled = !slots.every(Boolean);
    }
    function setReadout(hex) { q('[data-readout-chip]').style.background = hex || 'transparent'; q('[data-readout-hex]').textContent = hex || 'Hover a colour'; }
    function openPicker(i) {
      active = i; picker.hidden = false;
      q('[data-picker-title]').textContent = 'Colour ' + (i + 1);
      q('[data-remove]').hidden = !slots[i];
      coloursEl.querySelectorAll('.sbp__sw').forEach(function (s) { s.classList.toggle('sbp__sw--sel', s.dataset.hex === slots[i]); });
      setReadout(slots[i]); renderBlocks();
      loadCatalogue(cfg.catalogueUrl).catch(function () {}); // warm the catalogue while they choose
    }
    function closePicker() { active = null; picker.hidden = true; renderBlocks(); }
    function pick(hex) {
      if (active === null) return; slots[active] = hex;
      var next = null; for (var k = 1; k <= 4; k++) { var j = (active + k) % 4; if (!slots[j]) { next = j; break; } }
      if (next === null) { closePicker(); find.focus(); } else openPicker(next);
    }
    picker.addEventListener('click', function (e) { var s = e.target.closest('.sbp__sw'); if (s) pick(s.dataset.hex); });
    picker.addEventListener('mouseover', function (e) { var s = e.target.closest('.sbp__sw'); if (s) setReadout(s.dataset.hex); });
    picker.addEventListener('focusin', function (e) { var s = e.target.closest('.sbp__sw'); if (s) setReadout(s.dataset.hex); });
    q('[data-close]').addEventListener('click', closePicker);
    q('[data-remove]').addEventListener('click', function () { if (active !== null) { slots[active] = null; openPicker(active); } });
    root.addEventListener('keydown', function (e) { if (e.key === 'Escape' && active !== null) closePicker(); });

    function cardShell(it) {
      var a = document.createElement('a'); a.className = 'sbp__card sbp__card--loading'; a.href = ROOT + 'products/' + it.handle;
      a.innerHTML = '<div class="sbp__img"></div>';
      return a;
    }
    function fillCard(a, p, it) {
      var img = p.featured_image || (p.images && p.images[0]) || '';
      var price = '';
      if (cfg.showPrice) price = '<div class="sbp__price">' + (p.price_varies ? 'From ' : '') + cfg.money.format(p.price / 100) + '</div>';
      var extra = '';
      if (cfg.showScore) {
        var bar = it.pal.map(function (c) { return '<i style="background:' + c.hex + ';flex:' + c.w + '"></i>'; }).join('');
        extra = '<div class="sbp__makeup" aria-hidden="true">' + bar + '</div>';
      }
      a.classList.remove('sbp__card--loading');
      a.innerHTML = '<div class="sbp__img">' + (img ? '<img loading="lazy" src="' + sized(img, 500) + '" srcset="' + sized(img, 360) + ' 360w, ' + sized(img, 600) + ' 600w" sizes="(max-width: 749px) 50vw, 25vw" alt="' + esc(p.title) + '">' : '') + '</div>' +
        '<div class="sbp__info"><div class="sbp__title">' + esc(p.title) + '</div>' + price + extra + '</div>';
    }
    // Loads the next cfg.count matches. Prints that are unpublished, or sold out when
    // "Hide sold-out prints" is on, are skipped and the next-best match is fetched in their place.
    var loading = false, gen = 0;
    function showMore(fresh) {
      if (fresh === true) { gen++; loading = false; }
      if (loading) return Promise.resolve();
      loading = true; more.hidden = true;
      var added = 0, myGen = gen;
      function step() {
        if (myGen !== gen) return Promise.resolve(); // a newer search has started
        var need = cfg.count - added;
        if (need <= 0 || shown >= ranked.length) return Promise.resolve();
        var batch = ranked.slice(shown, shown + need); shown += batch.length;
        return Promise.all(batch.map(function (it) {
          var el = cardShell(it); list.appendChild(el);
          return fetch(ROOT + 'products/' + it.handle + '.js')
            .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
            .then(function (p) {
              if (myGen !== gen || (cfg.hideSoldOut && !p.available)) { el.remove(); return; }
              fillCard(el, p, it); added++;
            })
            .catch(function () { el.remove(); });
        })).then(step);
      }
      return step().then(function () {
        if (myGen !== gen) return;
        loading = false;
        more.hidden = shown >= ranked.length;
        if (!added && !list.children.length) status.textContent = 'No matching prints are available right now. Try different colours.';
      });
    }
    more.addEventListener('click', function () { showMore(); });

    find.addEventListener('click', function () {
      if (!slots.every(Boolean)) return;
      closePicker();
      results.hidden = false; list.innerHTML = ''; more.hidden = true;
      mini.innerHTML = slots.map(function (h) { return '<i style="background:' + h + '"></i>'; }).join('');
      status.textContent = 'Finding prints…';
      loadCatalogue(cfg.catalogueUrl).then(function (idx) {
        var u = slots.map(function (h) { var c = fromHex(h); return lab(c[0], c[1], c[2]); });
        ranked = idx.map(function (it) { return { handle: it.handle, pal: it.pal, score: score(u, it.pal) }; })
          .sort(function (a, b) { return b.score - a.score; });
        shown = 0; status.textContent = '';
        results.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        return showMore(true);
      }).catch(function () {
        status.textContent = cfg.design
          ? 'The art catalogue (glenn-art-finder-catalogue.json) could not be loaded. Check it is uploaded in Content > Files.'
          : "Colour search isn't available right now. Please try again shortly.";
      });
    });

    /* ---------- index builder (theme editor only) ---------- */
    var bld = q('[data-builder]');
    if (bld) {
      var btn = bld.querySelector('[data-build]'), prog = bld.querySelector('[data-progress]'), out = bld.querySelector('[data-output]'), dl = bld.querySelector('[data-download]');
      btn.addEventListener('click', function () {
        btn.disabled = true; prog.textContent = 'Loading catalogue…';
        var cat, failed = 0, added = 0;
        fetch(cfg.catalogueUrl, { cache: 'no-store' }).then(function (r) { if (!r.ok) throw new Error('catalogue not found'); return r.json(); })
          .then(function (c) {
            cat = c; cat.records = cat.records || [];
            // Add any prints in the collection that aren't in the catalogue yet. Descriptive fields
            // stay blank (the AI Art Finder fills those from the product's own tags and title).
            // Orientation stays 'square' like every other record, so the finder keeps reading
            // true orientation from Shopify tags.
            var have = {}; cat.records.forEach(function (r) { have[r.handle] = true; });
            prog.textContent = 'Checking for new prints…';
            return (function page(n) {
              return fetch(ROOT + 'collections/' + cfg.collection + '/products.json?limit=250&page=' + n).then(function (r) { return r.json(); }).then(function (j) {
                var ps = j.products || [];
                ps.forEach(function (p) {
                  if (have[p.handle] || !p.images || !p.images.length) return;
                  have[p.handle] = true; added++;
                  cat.records.push({ handle: p.handle, title: p.title, image: p.images[0].src, orientation: 'square',
                    colors: '', color_hex: '', color_intensity: '', subject: '', secondary: '', nz: '', environment: '',
                    style: '', mood: '', rooms: '', search: (p.title || '').toLowerCase() });
                });
                return ps.length && n < 40 ? page(n + 1) : null;
              });
            })(1);
          })
          .then(function () {
            var recs = cat.records, i = 0, done = 0, next = recs.slice();
            function worker() {
              if (i >= recs.length) return Promise.resolve();
              var k = i++, r = recs[k];
              return (r.image ? readImage(r.image) : Promise.resolve(null)).then(function (pal) {
                if (pal && pal.length) next[k] = enrichRecord(r, pal); else failed++;
                done++; prog.textContent = 'Reading artwork colours… ' + done + ' of ' + recs.length;
                return worker();
              });
            }
            return Promise.all(Array.from({ length: 8 }, worker)).then(function () { cat.records = next; });
          })
          .then(function () {
            cat.version = (cat.version || 1) + 1;
            cat.generated = new Date().toISOString().slice(0, 10);
            cat.count = cat.records.length;
            var txt = JSON.stringify(cat);
            out.value = txt; out.hidden = false;
            dl.href = URL.createObjectURL(new Blob([txt], { type: 'application/json' })); dl.hidden = false;
            prog.textContent = 'Done: ' + cat.records.length + ' prints in the catalogue' + (added ? ' (' + added + ' new added)' : ', no new prints found') + '.' + (failed ? ' ' + failed + ' images could not be read.' : '') + ' Download the file below and replace it in Content > Files.';
            btn.disabled = false;
          })
          .catch(function (e) { prog.textContent = 'Something went wrong: ' + e.message; btn.disabled = false; });
      });
    }

    renderBlocks();
  }

  function boot() { document.querySelectorAll('[data-shop-by-palette]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { var r = e.target.querySelector('[data-shop-by-palette]'); if (r) init(r); });
})();
